import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UsersRound, UserCheck, UserX, Building, IdCard, KeyRound, ShieldCheck, ArrowRight, UploadCloud, Plus } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import { ROLE_LABELS } from "@/constants/roles";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#101876", "#329112", "#f59e0b", "#e11d48", "#8b5cf6"];

export default function StaffDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get("/org/staff/dashboard");
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <DashboardLayout title="Staff Overview"><LoadingBlock label="Loading staff dashboard…" /></DashboardLayout>;
  }
  if (!data) return null;

  const t = data.totals;
  const roleData = (data.role_stats || []).map((r) => ({ name: ROLE_LABELS[r.role_code] || r.role_code, value: r.count }));
  const statusData = [
    { name: "Active", value: t.active },
    { name: "Inactive", value: t.inactive },
    { name: "Suspended", value: t.suspended },
  ];

  return (
    <DashboardLayout title="Staff Overview" breadcrumbs={["My Institute", "Staff"]}>
      <div className="space-y-6" data-testid={ADMIN.staffDashboardRoot}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm text-slate-600">Manage your organization's employees, faculty and learners.</div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/app/hospital/staff/import" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]" data-testid={ADMIN.staffImportBtn}>
              <UploadCloud className="h-3.5 w-3.5" /> Bulk upload
            </Link>
            <Link to="/app/hospital/staff/new" className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid={ADMIN.staffAddBtn}>
              <Plus className="h-3.5 w-3.5" /> Add staff
            </Link>
          </div>
        </div>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Staff" value={t.users} icon="UsersRound" tone="navy" testId="staff-kpi-total" index={0} />
          <StatCard label="Active" value={t.active} icon="UserCheck" tone="green" testId="staff-kpi-active" index={1} />
          <StatCard label="Inactive" value={t.inactive} icon="UserX" tone="slate" testId="staff-kpi-inactive" index={2} />
          <StatCard label="Suspended" value={t.suspended} icon="ShieldOff" tone="amber" testId="staff-kpi-suspended" index={3} />
        </section>

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Departments" value={t.departments} icon="Building" tone="navy" testId="staff-kpi-depts" index={4} />
          <StatCard label="Designations" value={t.designations} icon="IdCard" tone="green" testId="staff-kpi-desigs" index={5} />
          <StatCard label="Logins Created" value={t.logins_created} icon="KeyRound" tone="slate" testId="staff-kpi-logins" index={6} />
          <StatCard label="Department Heads" value={t.department_heads} icon="ShieldCheck" tone="green" testId="staff-kpi-heads" index={7} />
        </section>

        <section className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Staff by role</div>
            <h3 className="text-lg font-bold text-slate-900">Role distribution</h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roleData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#101876" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">By status</div>
            <h3 className="text-lg font-bold text-slate-900">Account status</h3>
            <div className="mt-2 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" innerRadius={40} outerRadius={72} paddingAngle={2}>
                    {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        {data.last_import && (
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Recent upload</div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">{data.last_import.file_name}</h3>
                <p className="text-xs text-slate-500">
                  {data.last_import.total_rows} rows · {data.last_import.successful_rows || 0} imported · {data.last_import.failed_rows || 0} failed
                </p>
              </div>
              <Link to="/app/hospital/staff/import" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#101876] hover:text-[#0c135c]">
                View import history <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}
